import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Phone, EnvelopeSimple, MapPin, PaperPlaneTilt } from "@phosphor-icons/react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full border-b border-forest/20 bg-transparent py-4 text-base text-forest placeholder:text-stone/70 transition-colors duration-300 focus:border-forest";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const { t } = useLang();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, { ...form, phone: form.phone || null });
      toast.success(t.contact.toastOk);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t.contact.toastErr);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-36" data-testid="contact-section">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="mb-6 text-xs font-semibold uppercase tracking-[0.3em] text-stone">{t.contact.eyebrow}</p>
            <h2 className="font-serif text-4xl leading-tight tracking-tight md:text-5xl lg:text-6xl">
              {t.contact.h2a}<em className="italic font-light text-moss">{t.contact.h2b}</em>
            </h2>
            <p className="mt-8 max-w-md text-base leading-relaxed text-moss md:text-lg">
              {t.contact.intro}
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 space-y-6">
              <a href="tel:+15619550794" className="group flex items-center gap-4" data-testid="contact-phone-link">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-forest/15 transition-colors duration-300 group-hover:bg-sage">
                  <Phone size={20} weight="duotone" />
                </span>
                <span className="link-underline font-serif text-xl italic md:text-2xl">+1 (561) 955-0794</span>
              </a>
              <a href="mailto:cecilekinnunen@gmail.com" className="group flex items-center gap-4" data-testid="contact-email-link">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-forest/15 transition-colors duration-300 group-hover:bg-sage">
                  <EnvelopeSimple size={20} weight="duotone" />
                </span>
                <span className="link-underline font-serif text-xl italic md:text-2xl">cecilekinnunen@gmail.com</span>
              </a>
              <div className="flex items-center gap-4" data-testid="contact-location">
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-forest/15">
                  <MapPin size={20} weight="duotone" />
                </span>
                <span className="font-serif text-xl italic md:text-2xl">Katy &amp; Houston, Texas</span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2} className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} className="rounded-[2rem] border border-forest/10 bg-sand p-8 md:p-12" data-testid="contact-form">
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div>
                <label htmlFor="contact-name" className="text-xs font-semibold uppercase tracking-[0.2em] text-stone">{t.contact.name}</label>
                <input
                  id="contact-name"
                  data-testid="contact-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder={t.contact.namePh}
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="text-xs font-semibold uppercase tracking-[0.2em] text-stone">{t.contact.email}</label>
                <input
                  id="contact-email"
                  data-testid="contact-email-input"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder={t.contact.emailPh}
                  className={inputCls}
                />
              </div>
            </div>
            <div className="mt-6">
              <label htmlFor="contact-phone" className="text-xs font-semibold uppercase tracking-[0.2em] text-stone">{t.contact.phone} <span className="normal-case tracking-normal text-stone/60">{t.contact.phoneOptional}</span></label>
              <input
                id="contact-phone"
                data-testid="contact-phone-input"
                value={form.phone}
                onChange={set("phone")}
                placeholder={t.contact.phonePh}
                className={inputCls}
              />
            </div>
            <div className="mt-6">
              <label htmlFor="contact-message" className="text-xs font-semibold uppercase tracking-[0.2em] text-stone">{t.contact.message}</label>
              <textarea
                id="contact-message"
                data-testid="contact-message-input"
                required
                rows={4}
                value={form.message}
                onChange={set("message")}
                placeholder={t.contact.messagePh}
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit-btn"
              className="mt-10 flex w-full items-center justify-center gap-3 rounded-full bg-forest px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-cream transition-all duration-300 hover:bg-moss hover:scale-[1.02] active:scale-95 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {sending ? t.contact.sending : t.contact.send}
              <PaperPlaneTilt size={16} weight="fill" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
